<template>
	<div class="page-content">
		<div class="button-wrapper">
			<ja-add-button @click.native="toggleMessageCreationModal">{{
				$t('addMessage')
			}}</ja-add-button>
		</div>
		<div>
			<ja-dashboard-container
				v-if="unreadMessagesCount"
				class="unread-container"
				:title="$t('titleUnreadMsg')"
				:number-msg="unreadMessagesCount"
			>
				<content>
					<ja-message
						v-for="(message, index) in unreadMessages"
						:key="index"
						:message="message"
						class="unread"
						@deleteMessage="openMessageDeletionModal(message)"
						@openMessage="openMessageDisplayModal(message)"
					>
						<template v-slot:message-icon>
							<svgicon name="unread" class="unread"></svgicon>
						</template>
					</ja-message>
				</content>
				<ja-pagination
					:current-page="unreadMessagesCurrentPage"
					:data-per-page="unreadMessagesPerPage"
					:data-count="unreadMessagesCount"
					@changePage="setUnreadMessagesPage"
				></ja-pagination>
			</ja-dashboard-container>

			<ja-dashboard-container
				v-if="allMessagesCount"
				class="read-container"
				:title="$t('titleAllMsg')"
				:number-msg="allMessagesCount"
			>
				<content>
					<ja-message
						v-for="message in combinedMessages"
						:key="message.id"
						:message="message"
						class="unread"
						@deleteMessage="openMessageDeletionModal(message)"
						@openMessage="openMessageDisplayModal(message)"
					>
						<template v-slot:message-icon>
							<svgicon
								:name="`${message.messageStatus}`.toLowerCase()"
								:class="`${message.messageStatus}`.toLowerCase()"
							></svgicon>
						</template>
					</ja-message>
				</content>
				<ja-pagination
					:current-page="allMessagesCurrentPage"
					:data-per-page="allMessagesPerPage"
					:data-count="allMessagesCount"
					@changePage="setAllMessagesPage"
				></ja-pagination>
			</ja-dashboard-container>
		</div>

		<div
			v-if="
				allMessages &&
					!allMessages.read.length &&
					!allMessages.done.length &&
					!allMessages.unread.length
			"
			class="no-results"
		>
			<h5>{{ $t('noResultsFound') }}</h5>
		</div>
		<ja-add-message-modal
			:show="showMessageCreationModal"
			@closeModal="toggleMessageCreationModal"
		></ja-add-message-modal>
		<ja-message-display-modal
			:show="showMessageDisplayModal"
			:message="selectedMessage"
			@closeMessageDisplayModal="closeMessageDisplayModal"
			@openMessageDeletionModal="openMessageDeletionModal"
			@refetchMessages="refetchMessages"
		></ja-message-display-modal>

		<ja-confirm-modal
			:show="showMessageDeletionModal"
			title=""
			class="confirm-modal--large"
			@confirm="deleteMessage(selectedMessage)"
			@closeModal="closeMessageDeletionModal()"
			@cancel="closeMessageDeletionModal()"
		>
			<h3>{{ $t('deleteMessageTitle') }}</h3>
		</ja-confirm-modal>
	</div>
</template>

<script>
import '@/components/icons/read';
import '@/components/icons/unread';
import '@/components/icons/star-outline';
import '@/components/icons/done';

import AddMessageModal from '../modals/AddMessageModal';
import AddButton from '../elements/AddButton';
import DashboardContainer from '../elements/DashboardContainer';
import Message from '../elements/Message';
import MessageDisplayModal from '../../Modal/MessageDisplayModal';
import Pagination from '../elements/Pagination';
import MessagesService from '@/services/MessagesService';
import gql from 'graphql-tag';
import ConfirmModal from '../../Modal/ConfirmModal.vue';
import AdminFacility from '@/mixins/AdminFacility';

export default {
	components: {
		jaMessage: Message,
		jaDashboardContainer: DashboardContainer,
		jaAddButton: AddButton,
		jaAddMessageModal: AddMessageModal,
		jaMessageDisplayModal: MessageDisplayModal,
		jaConfirmModal: ConfirmModal,
		jaPagination: Pagination
	},
	mixins: [AdminFacility],
	data() {
		return {
			showMessageCreationModal: false,
			showMessageDeletionModal: false,
			showMessageDisplayModal: false,
			selectedMessage: {},
			unreadMessagesCurrentPage: 1,
			unreadMessagesPerPage: 5,
			unreadMessagesCount: 0,
			allMessagesCurrentPage: 1,
			allMessagesPerPage: 15,
			allMessagesCount: 0,
			unreadMessages: 0,
			allMessages: null
		};
	},
	computed: {
		combinedMessages() {
			return [].concat(
				this.allMessages.unread,
				this.allMessages.read,
				this.allMessages.done
			);
		},
		userData() {
			return this.$store.getters.userData;
		},
		gqlSkipProperty() {
			return Math.max(
				(this.allMessagesCurrentPage - 1) * this.allMessagesPerPage,
				0
			);
		},
		gqlSkipUnreadMessagesProperty() {
			return Math.max(
				(this.unreadMessagesCurrentPage - 1) * this.unreadMessagesPerPage,
				0
			);
		}
	},
	watch: {
		selectedFacility() {
			this.refetchMessages();
		}
	},
	mounted() {
		this.signalRConnection.on('RefreshMessages', () => {
			this.refetchMessages();
		});
		if (this.selectedFacilityId) {
			this.refetchMessages();
		}
	},

	methods: {
		refetchMessages() {
			this.$store.commit('setLoading', true);
			this.closeMessageDisplayModal();
			this.closeMessageDeletionModal();
			this.getAllMessages();
			this.getUnreadMessages();
		},
		setMessages(data) {
			let result = {
				unread: data.messages.filter(m => m.messageStatus === 'Unread'),
				read: data.messages.filter(m => m.messageStatus === 'Read'),
				done: data.messages.filter(m => m.messageStatus === 'Done')
			};
			this.allMessagesCount = data.allReceivedMessages.length;

			this.allMessages = result;
			this.$store.commit('setLoading', false);
		},
		setUnreadMessages(data) {
			this.unreadMessages = data.unreadMessages;
			this.unreadMessagesCount = data.allUnreadReceivedMessages.length;
			this.$store.commit('setLoading', false);
		},
		toggleMessageCreationModal() {
			this.showMessageCreationModal = !this.showMessageCreationModal;
		},
		openMessageDisplayModal(message) {
			this.showMessageDisplayModal = true;
			this.selectedMessage = message;
		},
		closeMessageDisplayModal() {
			this.showMessageDisplayModal = false;
		},
		openMessageDeletionModal(message) {
			this.showMessageDeletionModal = true;
			this.selectedMessage = message;
		},
		closeMessageDeletionModal() {
			this.showMessageDeletionModal = false;
		},
		async setUnreadMessagesPage(page) {
			this.onLoadingStart();
			this.unreadMessagesCurrentPage = page;
			await this.getUnreadMessages();
			this.onLoadingStop();
		},
		async setAllMessagesPage(page) {
			this.onLoadingStart();
			this.allMessagesCurrentPage = page;
			await this.getAllMessages();
			this.onLoadingStop();
		},
		async getAllMessages() {
			await MessagesService.getAll(
				this.gqlSkipProperty,
				this.allMessagesPerPage,
				this.userData.userId,
				this.selectedFacilityId
			).then(messages => this.setMessages(messages));
		},
		async getUnreadMessages() {
			await MessagesService.getAllUnread(
				this.gqlSkipUnreadMessagesProperty,
				this.unreadMessagesPerPage,
				this.userData.userId,
				this.selectedFacilityId
			).then(data => this.setUnreadMessages(data));
		},
		async deleteMessage(message) {
			const response = await this.$apollo.mutate({
				mutation: gql`
					mutation($id: Int!) {
						deleteMessage(id: $id)
					}
				`,
				variables: {
					id: message.id
				}
			});
			if (response.data) {
				this.closeMessageDeletionModal();
				this.refetchMessages();
			}
		},
		onLoadingStart() {
			this.$emit('loadingStartForAdmin');
		},
		onLoadingStop() {
			this.$emit('loadingStopForAdmin');
		}
	}
};
</script>
<style lang="scss" scoped>
.page-content {
	flex-grow: 1;
	overflow-y: auto;
	overflow-x: hidden;
	min-height: 80vh;

	.button-wrapper {
		margin: 42px auto 16px auto;
		max-width: 1000px;
		text-align: right;
	}

	.dashboard-container {
		padding-bottom: 32px;

		&:last-child {
			padding-bottom: 84px;
		}

		.pagination {
			border-top: 1px solid $button-border;
		}
	}
	.facility-dropdown-container {
		margin: auto;
		width: 100%;
		margin: auto;
		margin-top: 16px;
		max-width: 1000px;
		margin-bottom: 32px;
		display: flex;
		align-items: center;
		justify-content: flex-start;
		justify-content: space-between;
	}
}
.no-results {
	display: flex;
	justify-content: center;
}
</style>
